'use client';

import { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import Link from 'next/link';

interface PriceInquiryFormProps {
  productId: string;
  productName: string;
}

export function PriceInquiryForm({ productId, productName }: PriceInquiryFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    city: '',
    kvkkAccepted: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.kvkkAccepted) {
      alert('Lütfen Kişisel Verilerin Korunması Metnini kabul edin.');
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('/api/price-inquiry', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          productId,
          productName,
        }),
      });

      if (!response.ok) throw new Error('Form gönderilemedi');

      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
        city: '',
        kvkkAccepted: false,
      });
    } catch (error) {
      console.error('Form gönderme hatası:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  return (
    <div className="p-4 sm:p-8">
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {/* Product Name (Readonly) */}
          <div className="w-full">
            <input
              type="text"
              value={productName}
              readOnly
              className="w-full px-5 py-3.5 bg-white border-transparent rounded shadow-sm text-gray-400 text-[15px] focus:ring-0 cursor-default font-medium outline-none"
            />
          </div>

          {/* Phone with Country Code */}
          <div className="w-full flex bg-white rounded shadow-sm overflow-hidden border-transparent">
            <div className="flex items-center pl-4 pr-2 bg-white cursor-pointer hover:bg-gray-50 border-r border-gray-100">
              <span className="text-lg leading-none mr-1">🇹🇷</span>
              <span className="text-gray-400 text-[10px]">▼</span>
            </div>
            <input
              type="tel"
              name="phone"
              placeholder="Telefon"
              value={formData.phone}
              onChange={handleChange}
              required
              className="flex-1 px-4 py-3.5 border-transparent bg-transparent text-[15px] text-gray-800 placeholder-gray-400 focus:ring-0 outline-none font-medium"
            />
          </div>

          {/* Name */}
          <div className="w-full">
            <input
              type="text"
              name="name"
              placeholder="İsim"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-5 py-3.5 bg-white border-transparent rounded shadow-sm text-[15px] text-gray-800 placeholder-gray-400 focus:ring-0 outline-none font-medium"
            />
          </div>

          {/* Email */}
          <div className="w-full">
            <input
              type="email"
              name="email"
              placeholder="Eposta"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-5 py-3.5 bg-white border-transparent rounded shadow-sm text-[15px] text-gray-800 placeholder-gray-400 focus:ring-0 outline-none font-medium"
            />
          </div>

          {/* Message Field (Replacing Country) */}
          <div className="w-full sm:col-span-2">
            <textarea
              name="message"
              placeholder="Mesajınız"
              value={formData.message}
              onChange={handleChange}
              required
              rows={3}
              className="w-full px-5 py-3.5 bg-white border-transparent rounded shadow-sm text-[15px] text-gray-800 placeholder-gray-400 focus:ring-0 outline-none font-medium resize-none"
            />
          </div>

          {/* City (Text input instead of Select) */}
          <div className="w-full sm:col-span-2">
            <input
              type="text"
              name="city"
              placeholder="Şehir"
              value={formData.city}
              onChange={handleChange}
              required
              className="w-full px-5 py-3.5 bg-white border-transparent rounded shadow-sm text-[15px] text-gray-800 placeholder-gray-400 focus:ring-0 outline-none font-medium"
            />
          </div>
        </div>

        {/* Footer Area */}
        <div className="mt-8 pt-4 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-start gap-3 sm:max-w-[350px]">
            <input
              type="checkbox"
              id="kvkkAccepted"
              name="kvkkAccepted"
              checked={formData.kvkkAccepted}
              onChange={handleChange}
              required
              className="mt-1 w-5 h-5 border-gray-300 rounded text-red-800 focus:ring-red-800 cursor-pointer"
            />
            <label htmlFor="kvkkAccepted" className="text-[13px] text-[#777] leading-[1.6] font-medium cursor-pointer">
              <Link href="/gizlilik-politikasi" className="underline hover:text-gray-900 transition-colors font-bold">
                Kişisel Verilerin Korunması Metnini
              </Link>
              {' '}okudum ve kabul ediyorum.
            </label>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full sm:w-[350px] bg-white hover:bg-gray-50 text-[#333] font-medium py-4 px-8 rounded shadow-sm border border-gray-100 transition-colors flex items-center justify-center gap-4 group"
          >
            {isSubmitting ? 'Gönderiliyor...' : 'Gönder'}
            {!isSubmitting && <ArrowRight className="h-5 w-5 text-gray-800 transition-transform group-hover:translate-x-1" />}
          </button>
        </div>

        {submitStatus === 'success' && (
          <p className="text-green-600 text-sm text-center mt-4 bg-green-50 py-2 rounded">
            Formunuz başarıyla gönderildi. En kısa sürede size dönüş yapacağız.
          </p>
        )}

        {submitStatus === 'error' && (
          <p className="text-red-600 text-sm text-center mt-4 bg-red-50 py-2 rounded">
            Bir hata oluştu. Lütfen daha sonra tekrar deneyin.
          </p>
        )}
      </form>
    </div>
  );
} 